/**
 * The single HTTP entry point for the portal (spec §11). All API calls go
 * through here — never import `fetch` directly in pages/components (spec §8.4).
 *
 * Invariants:
 *  - `credentials: 'include'` always (cookie-first session, spec §3, §11.1).
 *  - No Authorization bearer token from browser storage (spec §17.1).
 *  - `X-CSRF-Token` on POST/PATCH/DELETE from the in-memory store (spec §11.2).
 *  - 15s timeout for JSON calls; responses parsed with Zod (spec §11.1).
 *  - No automatic retry here — React Query owns safe-GET retry policy.
 */
import type { z } from 'zod';
import { env } from '../lib/env';
import { ApiHttpError, NetworkError } from './apiErrors';
import { apiErrorSchema, envelopeShapeSchema } from './apiSchemas';
import { getCsrfToken } from './csrf';

const JSON_TIMEOUT_MS = 15_000;
const MUTATING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions<T> {
  /** Zod schema for the inner `data` payload. Omit for void/204 responses. */
  schema?: z.ZodType<T>;
  /** JSON request body (auto-serialised). */
  body?: unknown;
  /** Caller AbortSignal (e.g. React Query) combined with the internal timeout. */
  signal?: AbortSignal;
}

function buildUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  const base = env.apiBaseUrl;
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

interface CombinedSignal {
  signal: AbortSignal;
  cleanup: () => void;
}

/** Combines an external signal with an internal timeout, robustly across runtimes. */
function combineSignals(external: AbortSignal | undefined, timeoutMs: number): CombinedSignal {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(new DOMException('Request timed out', 'TimeoutError'));
  }, timeoutMs);

  const onExternalAbort = () => controller.abort(external?.reason);
  if (external) {
    if (external.aborted) controller.abort(external.reason);
    else external.addEventListener('abort', onExternalAbort, { once: true });
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timer);
      external?.removeEventListener('abort', onExternalAbort);
    },
  };
}

async function parseError(response: Response): Promise<ApiHttpError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    return new ApiHttpError(response.status, 'UNKNOWN', null);
  }
  const parsed = apiErrorSchema.safeParse(body);
  if (parsed.success) return ApiHttpError.fromBody(response.status, parsed.data);
  return new ApiHttpError(response.status, 'UNKNOWN', null);
}

export async function request<T = void>(
  method: HttpMethod,
  path: string,
  options: RequestOptions<T> = {},
): Promise<T> {
  const { schema, body, signal } = options;
  const headers: Record<string, string> = { Accept: 'application/json' };

  let serialisedBody: string | undefined;
  if (body !== undefined && method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    serialisedBody = JSON.stringify(body);
  }
  if (MUTATING.has(method)) {
    const token = getCsrfToken();
    if (token) headers['X-CSRF-Token'] = token;
  }

  const { signal: combined, cleanup } = combineSignals(signal, JSON_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      method,
      credentials: 'include',
      headers,
      body: serialisedBody,
      signal: combined,
    });
  } catch (err) {
    cleanup();
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new NetworkError('timeout');
    }
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err; // caller/React Query cancellation — let it propagate
    }
    throw new NetworkError(err instanceof Error ? err.message : 'network');
  } finally {
    cleanup();
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === 204 || !schema) {
    return undefined as T;
  }

  const raw: unknown = await response.json();
  const outer = envelopeShapeSchema.safeParse(raw);
  if (!outer.success) {
    throw new ApiHttpError(response.status, 'INVALID_RESPONSE', null, outer.error.message);
  }
  const parsed = schema.safeParse(outer.data.data);
  if (!parsed.success) {
    // Schema drift / unexpected shape — surface as a controlled error, never render raw.
    throw new ApiHttpError(response.status, 'INVALID_RESPONSE', null, parsed.error.message);
  }
  return parsed.data;
}

export const httpClient = {
  get: <T>(path: string, schema: z.ZodType<T>, signal?: AbortSignal): Promise<T> =>
    request<T>('GET', path, { schema, signal }),
  post: <T = void>(path: string, options: RequestOptions<T> = {}): Promise<T> =>
    request<T>('POST', path, options),
  patch: <T = void>(path: string, options: RequestOptions<T> = {}): Promise<T> =>
    request<T>('PATCH', path, options),
  del: <T = void>(path: string, options: RequestOptions<T> = {}): Promise<T> =>
    request<T>('DELETE', path, options),
};
